import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Drawer, Button, Spinner, RichContent } from '../common';
import {
  useBookCompanionChat,
  useSendCompanionMessage,
  useApplyCompanionProposal,
  useDismissCompanionProposal,
  useRevertCompanionProposal,
  useStartCompanionChat,
  CompanionProposal,
  CompanionMessage,
  CompanionFocus,
} from '../../hooks/useBookCompanion';

interface BookCompanionProps {
  bookId: string;
  onClose: () => void;
  // Surface the chat was opened from — picks the lens and keeps a separate
  // thread per surface (plan vs reader). Omit for the default thread.
  focus?: CompanionFocus;
  // Drawer header copy, so each surface can frame the same chat in its own words.
  title?: string;
  subtitle?: string;
  // Applied/reverted proposals change book state — let the host page refetch.
  onBookChanged?: () => void;
}

// A few human-readable lines pulled out of the impact blob (shape varies by kind).
// `summary`, `note` and `warnings` are server-provided (already in the book's
// language); the count lines are localized here.
function impactLines(p: CompanionProposal, t: TFunction): string[] {
  const i = p.impact || {};
  const lines: string[] = [];
  if (typeof i.summary === 'string') lines.push(i.summary as string);
  if (typeof i.total_occurrences === 'number') {
    const locs = Array.isArray(i.locations) ? (i.locations as unknown[]).length : 0;
    lines.push(
      t('companion.impact.occurrences', {
        count: i.total_occurrences,
        locations: locs,
        defaultValue: '{{count}} occurrence(s) across {{locations}} location(s)',
      })
    );
  }
  // Rename now expands inflected forms (LT/RU cases) into one changeset; show how
  // many forms matched and list the ones that actually occur so the author can vet
  // the expansion before approving the whole set in a single action.
  if (typeof i.forms_total === 'number' && i.forms_total > 1) {
    lines.push(
      t('companion.impact.forms', {
        matched: i.forms_matched ?? 0,
        total: i.forms_total,
        defaultValue: 'Matched {{matched}} of {{total}} inflected forms',
      })
    );
    if (Array.isArray(i.forms)) {
      (i.forms as Array<{ from?: string; to?: string; count?: number }>)
        .filter((f) => f && (f.count ?? 0) > 0)
        .forEach((f) => lines.push(`• ${f.from} → ${f.to} (${f.count})`));
    }
  }
  if (typeof i.replaced === 'number')
    lines.push(
      t('companion.impact.replaced', {
        count: i.replaced,
        locked: i.kept_locked ?? 0,
        defaultValue: 'Replaces {{count}} plan point(s), keeps {{locked}} locked',
      })
    );
  if (Array.isArray(i.added) && (i.added as unknown[]).length > 0) {
    const points = (i.added as unknown[]).filter((p): p is string => typeof p === 'string');
    if (typeof i.existing === 'number')
      lines.push(
        t('companion.impact.planPointsAdded', {
          count: points.length,
          existing: i.existing,
          defaultValue: 'Adds {{count}} plan point(s) to {{existing}} existing',
        })
      );
    points.forEach((p) => lines.push(`• ${p}`));
  }
  if (Array.isArray(i.scenes_removed))
    lines.push(
      t('companion.impact.scenesRemoved', {
        count: (i.scenes_removed as unknown[]).length,
        defaultValue: '{{count}} scene(s) removed',
      })
    );
  if (typeof i.scenes_moved === 'number')
    lines.push(
      t('companion.impact.scenesMoved', {
        count: i.scenes_moved,
        defaultValue: '{{count}} scene(s) move',
      })
    );
  if (typeof i.words_lost === 'number' && i.words_lost > 0)
    lines.push(
      t('companion.impact.wordsAffected', {
        count: i.words_lost,
        defaultValue: '{{count}} words affected',
      })
    );
  if (typeof i.note === 'string' && p.kind !== 'park_idea') lines.push(i.note as string);
  if (Array.isArray(i.warnings)) (i.warnings as string[]).forEach((w) => lines.push(`⚠ ${w}`));
  return lines;
}

function ProposalCard({
  proposal,
  onApply,
  onDismiss,
  onRevert,
  busy,
}: {
  proposal: CompanionProposal;
  onApply: () => void;
  onDismiss: () => void;
  onRevert: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const lines = impactLines(proposal, t);
  const isPending = proposal.status === 'pending';
  // Resolved (applied/dismissed/reverted) cards collapse to a single line to keep
  // the changelog scannable; they expand on demand. Pending cards stay open.
  const [expanded, setExpanded] = useState(false);

  const kindLabel = t(`companion.kindLabels.${proposal.kind}`, {
    defaultValue: proposal.kind.replace(/_/g, ' '),
  });

  const resolved: Record<string, { icon: string; cls: string; label: string }> = {
    applied: {
      icon: '✓',
      cls: 'text-green-600 dark:text-green-400',
      label: t('companion.applied', '✓ Applied'),
    },
    dismissed: { icon: '×', cls: 'text-gray-400', label: t('companion.dismissed', 'Dismissed') },
    reverted: { icon: '↩', cls: 'text-gray-400', label: t('companion.reverted', 'Reverted') },
  };

  if (!isPending && !expanded) {
    const meta = resolved[proposal.status] ?? resolved.dismissed;
    return (
      <div className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-2.5 py-1 text-xs">
        <span className={`shrink-0 font-semibold ${meta.cls}`} title={meta.label}>
          {meta.icon}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="min-w-0 flex-1 truncate text-left font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          title={proposal.title}
        >
          {proposal.title}
        </button>
        {proposal.status === 'applied' && proposal.revertable && (
          <button
            type="button"
            onClick={onRevert}
            disabled={busy}
            className="shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            {t('companion.revert', 'Revert')}
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={t('companion.expand', 'Expand')}
          className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          ▾
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/20 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-gray-900 dark:text-gray-100">{proposal.title}</span>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="rounded-full bg-teal-100 dark:bg-teal-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-teal-700 dark:text-teal-300">
            {kindLabel}
          </span>
          {!isPending && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label={t('companion.collapse', 'Collapse')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ▴
            </button>
          )}
        </div>
      </div>
      {proposal.rationale && (
        <p className="mt-1 italic text-gray-600 dark:text-gray-400">{proposal.rationale}</p>
      )}
      {lines.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
          {lines.map((l, idx) => (
            <li key={idx}>{l}</li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        {proposal.status === 'pending' && (
          <>
            <Button size="sm" variant="primary" onClick={onApply} isLoading={busy}>
              {t('companion.apply', 'Apply')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}>
              {t('companion.dismiss', 'Dismiss')}
            </Button>
          </>
        )}
        {proposal.status === 'applied' && (
          <>
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              {t('companion.applied', '✓ Applied')}
            </span>
            {proposal.revertable && (
              <Button size="sm" variant="ghost" onClick={onRevert} disabled={busy}>
                {t('companion.revert', 'Revert')}
              </Button>
            )}
          </>
        )}
        {proposal.status === 'dismissed' && (
          <span className="text-xs text-gray-400">{t('companion.dismissed', 'Dismissed')}</span>
        )}
        {proposal.status === 'reverted' && (
          <span className="text-xs text-gray-400">{t('companion.reverted', 'Reverted')}</span>
        )}
      </div>
    </div>
  );
}

export default function BookCompanion({
  bookId,
  onClose,
  focus,
  title,
  subtitle,
  onBookChanged,
}: BookCompanionProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('companion.title', 'Chat about this');
  const resolvedSubtitle =
    subtitle ??
    t(`companion.subtitle.${focus ?? 'default'}`, {
      defaultValue: 'Talk through the book — editor & sparring partner',
    });

  const { data: chat, isLoading } = useBookCompanionChat(bookId, focus);
  const send = useSendCompanionMessage(bookId, focus);
  const apply = useApplyCompanionProposal(bookId, focus, onBookChanged);
  const dismiss = useDismissCompanionProposal(bookId, focus);
  const revert = useRevertCompanionProposal(bookId, focus, onBookChanged);
  const startFresh = useStartCompanionChat(bookId, focus);

  const [draft, setDraft] = useState('');
  const [busyProposalId, setBusyProposalId] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages.length, send.isPending]);

  const submit = () => {
    const content = draft.trim();
    if (!content || send.isPending) return;
    setDraft('');
    send.mutate(content);
  };

  const applied = chat?.appliedProposals ?? [];

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      width="lg"
      resizable
      storageKey="companionDrawerWidth"
      bodyClassName="flex flex-1 flex-col min-h-0"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Session changelog */}
        {applied.length > 0 && (
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
            {t('companion.changelog', {
              count: applied.length,
              defaultValue:
                '{{count}} change(s) applied to the book. Closing the chat loses nothing.',
            })}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 space-y-3 overflow-y-auto p-3">
          {isLoading && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}

          {chat?.messages.map((msg: CompanionMessage) => (
            <div key={msg.id} className="space-y-2">
              <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <RichContent content={msg.content ?? ''} />
                  ) : (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  )}
                </div>
              </div>
              {msg.proposals.map((p) => (
                <ProposalCard
                  key={p.id}
                  proposal={p}
                  busy={busyProposalId === p.id}
                  onApply={() => {
                    setBusyProposalId(p.id);
                    apply.mutate(p.id, { onSettled: () => setBusyProposalId(null) });
                  }}
                  onDismiss={() => {
                    setBusyProposalId(p.id);
                    dismiss.mutate(p.id, { onSettled: () => setBusyProposalId(null) });
                  }}
                  onRevert={() => {
                    setBusyProposalId(p.id);
                    revert.mutate(p.id, { onSettled: () => setBusyProposalId(null) });
                  }}
                />
              ))}
            </div>
          ))}

          {send.isPending && (
            <div className="flex justify-start">
              <div className="rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-700">
                <Spinner size="sm" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={2}
              placeholder={t('companion.placeholder', 'What feels off? What are you stuck on?')}
              className="flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            <Button
              size="sm"
              variant="primary"
              onClick={submit}
              isLoading={send.isPending}
              disabled={!draft.trim()}
            >
              {t('companion.send', 'Send')}
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
            <span>
              {t('companion.reviewNote', 'Proposals are reviewed before anything changes.')}
            </span>
            <button
              type="button"
              onClick={() => startFresh.mutate()}
              disabled={startFresh.isPending}
              className="hover:text-gray-600 dark:hover:text-gray-300"
            >
              {t('companion.startOver', 'Start over')}
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
